import { useEffect, type ReactNode } from 'react';
import { DOW, MON_GEN, daysBetween, fmtD, holidays, plural, today0, whyShift, ymd, type Deadline } from '@biuro/shared';

const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

export const Icon = {
  pulpit: () => (<svg viewBox="0 0 24 24" {...S}><rect x="3" y="3" width="7.5" height="9" rx="1.8" /><rect x="13.5" y="3" width="7.5" height="5.5" rx="1.8" /><rect x="13.5" y="11.5" width="7.5" height="9.5" rx="1.8" /><rect x="3" y="15" width="7.5" height="6" rx="1.8" /></svg>),
  terminy: () => (<svg viewBox="0 0 24 24" {...S}><rect x="3" y="4.5" width="18" height="16.5" rx="2.5" /><path d="M3 9.5h18M8 2.5v4M16 2.5v4M7.5 14h2M11 14h2M14.5 14h2M7.5 17.5h2" /></svg>),
  faktury: () => (<svg viewBox="0 0 24 24" {...S}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.6 2.6 3.9 5.6 3.9 9s-1.3 6.4-3.9 9c-2.6-2.6-3.9-5.6-3.9-9S9.4 5.6 12 3z" /></svg>),
  miesiac: () => (<svg viewBox="0 0 24 24" {...S}><path d="M4 7.5V18a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5" /><path d="M3 4.5h18v3H3z" /><path d="M9.5 12h5" /></svg>),
  firma: () => (<svg viewBox="0 0 24 24" {...S}><rect x="3" y="7" width="18" height="13" rx="2.5" /><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 13h18" /></svg>),
  plus: () => (<svg viewBox="0 0 24 24" {...S} strokeWidth={2}><path d="M12 5v14M5 12h14" /></svg>),
  upload: () => (<svg viewBox="0 0 24 24" {...S}><path d="M12 15V3M7 8l5-5 5 5" /><path d="M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" /></svg>),
  download: () => (<svg viewBox="0 0 24 24" {...S}><path d="M12 3v12M7 10l5 5 5-5" /><path d="M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" /></svg>),
  ext: () => (<svg viewBox="0 0 24 24" {...S}><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></svg>),
  mail: () => (<svg viewBox="0 0 24 24" {...S}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></svg>),
  prev: () => (<svg viewBox="0 0 24 24" {...S} strokeWidth={2}><path d="m15 5-7 7 7 7" /></svg>),
  next: () => (<svg viewBox="0 0 24 24" {...S} strokeWidth={2}><path d="m9 5 7 7-7 7" /></svg>),
  check: () => (<svg viewBox="0 0 24 24" {...S} strokeWidth={3}><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>),
  clock: () => (<svg viewBox="0 0 24 24" {...S} strokeWidth={2.4}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></svg>),
  alert: () => (<svg viewBox="0 0 24 24" {...S} strokeWidth={3}><path d="M12 6v7M12 17.5v.5" /></svg>),
  coins: () => (<svg viewBox="0 0 24 24" {...S}><ellipse cx="9" cy="7" rx="6" ry="3" /><path d="M3 7v5c0 1.7 2.7 3 6 3s6-1.3 6-3V7" /><path d="M9 15v2c0 1.7 2.7 3 6 3s6-1.3 6-3v-5c0-1.7-2.7-3-6-3" /></svg>),
  send: () => (<svg viewBox="0 0 24 24" {...S}><path d="M21 3 10 14" /><path d="m21 3-7 18-4-7-7-4z" /></svg>),
  doc: () => (<svg viewBox="0 0 24 24" {...S}><path d="M14 3H6.5A2.5 2.5 0 0 0 4 5.5v13A2.5 2.5 0 0 0 6.5 21h11a2.5 2.5 0 0 0 2.5-2.5V9z" /><path d="M14 3v6h6M8.5 13h7M8.5 17h5" /></svg>),
  // znak aplikacji: kartka z ptaszkiem
  logo: () => (<svg viewBox="0 0 24 24" {...S} strokeWidth={2.4}><path d="M5 4h14v16H5z" /><path d="m8.5 12.5 2.5 2.5 4.5-5" /></svg>),
};

const MON_SHORT = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];
const DOW_SHORT = ['nd', 'pn', 'wt', 'śr', 'cz', 'pt', 'sb'];

/** Niedziela albo święto. */
export const isRedDay = (d: Date) => d.getDay() === 0 || holidays(d.getFullYear()).has(ymd(d));

export const todayLong = () => {
  const t = today0();
  return `${DOW[t.getDay()]}, ${t.getDate()} ${MON_GEN[t.getMonth()]} ${t.getFullYear()}`;
};

export function Brand({ sub = 'EVA:LUATE' }: { sub?: string }) {
  return (
    <div className="brand">
      <span className="mark" aria-hidden="true"><Icon.logo /></span>
      <div><b>Biuro</b><span>{sub}</span></div>
    </div>
  );
}

export function countdown(due: Date, done: boolean): [string, string] {
  const n = daysBetween(today0(), due);
  if (done) return ['zrobione', 'ok'];
  if (n === 0) return ['dziś', 'hi'];
  if (n === 1) return ['jutro', 'warn'];
  if (n < 0) return [`${-n} ${plural(-n, 'dzień', 'dni', 'dni')} po terminie`, 'bad'];
  return [`za ${n} dni`, n <= 3 ? 'warn' : ''];
}

export function DayNum({ d, tone = '' }: { d: Date; tone?: string }) {
  return (
    <div className={`daynum ${tone}`}>
      <span className="dn">{d.getDate()}</span>
      <span className="dw">{DOW_SHORT[d.getDay()]} {MON_SHORT[d.getMonth()]}</span>
    </div>
  );
}

export function DeadlineRow({ d, done, extra, onToggle, onEdit }: {
  d: Deadline; done: boolean; extra?: string; onToggle?: (v: boolean) => void; onEdit?: () => void;
}) {
  const closed = done || d.before;
  const [cd, pc] = countdown(d.due, closed);
  const n = daysBetween(today0(), d.due);
  const tone = closed ? 'done' : n < 0 ? 'over' : n <= 3 ? 'soon' : '';
  return (
    <div className={`dl ${closed ? 'is-done' : ''}`}>
      <DayNum d={d.due} tone={tone} />
      <div className="dl-body">
        <div className="dl-t">{d.title} <span className="code-name">{d.form}</span></div>
        <div className="dl-s">
          {[d.sub, extra, d.shifted ? `przesunięte z ${fmtD(d.orig)} (${whyShift(d.orig)})` : ''].filter(Boolean).join('; ')}
          {onEdit && <>{' '}<button className="linkish" onClick={onEdit}>Edytuj</button></>}
        </div>
        {d.note && !closed && <div className="dl-n">{d.note}</div>}
      </div>
      <div className="dl-side">
        <span className={`cd ${pc}`}>{cd}</span>
        {!d.before && onToggle && (
          <label className="chk">
            <input type="checkbox" checked={done} onChange={(e) => onToggle(e.target.checked)} />
            <span>Zrobione</span>
          </label>
        )}
      </div>
    </div>
  );
}

/** Krótka nazwa terminu do kalendarza. */
export function shortLabel(d: Deadline): string {
  const id = d.id;
  if (id.startsWith('docs-')) return 'Do biura';
  if (id.startsWith('zus-')) return 'ZUS';
  if (id.startsWith('ryczalt-')) return 'Ryczałt';
  if (id.startsWith('jpk-')) return 'VAT';
  if (id.startsWith('infakt-')) return 'inFakt';
  if (id.startsWith('vatue-')) return 'VAT-UE';
  if (id.startsWith('oss-')) return 'OSS';
  if (id.startsWith('bdo-')) return 'BDO';
  if (id.startsWith('pit28-')) return 'PIT-28';
  if (id.startsWith('zdrow-')) return 'Zdrowotna';
  if (id.startsWith('rodo-')) return 'Przegląd RODO';
  if (id.startsWith('ksef-')) return 'KSeF';
  return d.title;
}

export function Sheet({ title, onClose, children, footer, dismissOnBackdrop = true }: {
  title: string; onClose: () => void; children: ReactNode; footer?: ReactNode;
  /** Kliknięcie obok okna zamyka je – wyłącz, gdy przypadkowe zamknięcie gubi wpisane dane. */
  dismissOnBackdrop?: boolean;
}) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', k);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return (
    <div className="sheet" onMouseDown={(e) => dismissOnBackdrop && e.target === e.currentTarget && onClose()}>
      <div className="sheet-panel" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet-head">
          <h2>{title}</h2>
          <button className="btn ghost sm" onClick={onClose}>Zamknij</button>
        </div>
        {children}
        {footer && <div className="sheet-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <b>{title}</b>
      {children}
    </div>
  );
}

/** Przycisk usuwania z potwierdzeniem w dwóch krokach (bez okien dialogowych przeglądarki). */
export function DeleteButton({ onConfirm, label = 'Usuń' }: { onConfirm: () => void; label?: string }) {
  return (
    <button
      type="button"
      className="btn danger"
      onClick={(e) => {
        const b = e.currentTarget;
        if (b.dataset.armed) return onConfirm();
        b.dataset.armed = '1';
        b.textContent = 'Na pewno usunąć?';
      }}
    >
      {label}
    </button>
  );
}

export const timeAgo = (iso?: string | null) => {
  if (!iso) return 'nigdy';
  const d = new Date(iso);
  return `${fmtD(d)}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
